// The client's own plan in the portal (finance-pricing target): how each line and each job in progress reads, in
// Italian, or English with ?lang=en (useLocale). Only the client's own lines and agreed prices arrive; nothing here
// sees a "from" price, a work value or a reason. Pure so it can be tested.
import { httpStatusOf } from "@/core/http/HttpError";
import type { Locale } from "@/hooks/useLocale";

export interface PortalPlanLine {
  id: string;
  label: string;
  kind: "monthly" | "post_package" | "one_time";
  quantity: number;
  priceMinor: number;
}

export interface PortalPlanContract {
  id: string;
  name: string;
  kind: "one_off" | "recurring";
  /** When it runs from; later than today for a version accepted to start then. */
  effectiveFrom: string | null;
  lines: PortalPlanLine[];
}

export interface PortalPlanJob {
  id: string;
  kind: "monthly" | "post_package" | "one_off" | "extra";
  contractName: string;
  label: string | null;
  sequence: number;
  startsOn: string | null;
  endsOn: string | null;
  postsCounted: number;
  postsTarget: number | null;
  amountMinor: number;
}

export interface PortalPlan {
  clientName: string;
  currency: string;
  contracts: PortalPlanContract[];
  current: PortalPlanJob[];
  today: string;
}

const WORDS: Record<Locale, { monthly: string; oneTime: string; from: string; project: string; inProgress: string; extra: string; period: string }> = {
  it: { monthly: "al mese", oneTime: "una tantum", from: "dal", project: "progetto", inProgress: "in lavorazione", extra: "Extra", period: "Periodo" },
  en: { monthly: "a month", oneTime: "one-off", from: "since", project: "project", inProgress: "in progress", extra: "Extra", period: "Period" },
};

/** "1 ott" in Italian, "1 Oct" in English. */
export const shortDate = (iso: string | null, locale: Locale): string =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(locale === "it" ? "it-IT" : "en-GB", { day: "numeric", month: "short" }) : "";

export function lineKind(line: PortalPlanLine, locale: Locale): string {
  if (line.kind === "post_package") return locale === "it" ? `${line.quantity} post a pacchetto` : `${line.quantity} posts a package`;
  return line.kind === "monthly" ? WORDS[locale].monthly : WORDS[locale].oneTime;
}

export const contractTitle = (contract: PortalPlanContract, locale: Locale): string => (contract.kind === "one_off" ? `${contract.name} (${WORDS[locale].project})` : contract.name);

/** "dal 1 ott": since when a recurring contract runs, or will. */
export const contractSince = (contract: PortalPlanContract, locale: Locale): string | null =>
  contract.kind === "recurring" && contract.effectiveFrom ? `${WORDS[locale].from} ${shortDate(contract.effectiveFrom, locale)}` : null;

export function jobText(p: PortalPlanJob, locale: Locale): string {
  if (p.kind === "post_package") {
    const target = p.postsTarget ?? 0;
    return locale === "it" ? `Pacchetto ${p.sequence}: ${p.postsCounted} di ${target} post pubblicati` : `Package ${p.sequence}: ${p.postsCounted} of ${target} posts published`;
  }
  if (p.kind === "extra") return `${WORDS[locale].extra}: ${p.label ?? p.contractName}`;
  if (p.kind === "one_off") return `${p.contractName}: ${WORDS[locale].inProgress}`;
  return `${WORDS[locale].period} ${shortDate(p.startsOn, locale)} – ${shortDate(p.endsOn, locale)}`;
}

/** The plan read retries a failure with no answer or a server error twice; a refusal (403 not a contact, 401) never. */
export const retryPlan = (failureCount: number, error: unknown): boolean => {
  const status = httpStatusOf(error);
  return (status === null || status >= 500) && failureCount < 2;
};
